'use client';

/**
 * Case Notes Panel Component
 *
 * Multi-stakeholder case notes with PHI shielding:
 * - Service provider notes (progress, outcomes)
 * - Employee/member notes (feedback, requests)
 * - Trust program manager notes (coordination, oversight)
 * - Employer notes (PHI-shielded view)
 *
 * Supports different note types and privacy levels
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  MessageSquare,
  Plus,
  Clock,
  User,
  Building2,
  Shield,
  Lock,
  AlertCircle,
  Loader2,
  Send,
  Eye,
  EyeOff,
  Users,
  Briefcase,
  Heart,
  Phone,
  FileText,
  ChevronDown,
  ChevronUp,
  Filter,
  Calendar,
} from 'lucide-react';
import { apiClient } from '@/services/apiClient';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

type NoteType = 'contact' | 'update' | 'outcome' | 'internal' | 'follow_up' | 'escalation';
type StakeholderView = 'all' | 'provider' | 'member' | 'program_manager' | 'employer';

interface CaseNote {
  id: number;
  referral_id: number;
  note_type: NoteType;
  note_content: string;
  is_private: boolean;
  user_id: number;
  user_name?: string;
  user_role?: string;
  stakeholder_type?: string;
  created_at: string;
  updated_at?: string;
}

interface CaseNotesPanelProps {
  referralId: number;
  currentUserRole?: 'provider' | 'member' | 'program_manager' | 'employer' | 'admin';
  readOnly?: boolean;
  showPrivateNotes?: boolean;
  className?: string;
}

const NOTE_TYPES: { value: NoteType; label: string; icon: React.ElementType; description: string }[] = [
  { value: 'contact', label: 'Contact Attempt', icon: Phone, description: 'Record contact attempts with member or provider' },
  { value: 'update', label: 'Progress Update', icon: FileText, description: 'Document service progress and updates' },
  { value: 'outcome', label: 'Service Outcome', icon: Heart, description: 'Record service completion and outcomes' },
  { value: 'internal', label: 'Internal Note', icon: Lock, description: 'Team-only notes (not visible to external parties)' },
  { value: 'follow_up', label: 'Follow-up Required', icon: Calendar, description: 'Schedule and track follow-up actions' },
  { value: 'escalation', label: 'Escalation', icon: AlertCircle, description: 'Document issues requiring escalation' },
];

const STAKEHOLDER_COLORS: Record<string, string> = {
  provider: 'bg-blue-100 text-blue-700',
  member: 'bg-green-100 text-green-700',
  program_manager: 'bg-purple-100 text-purple-700',
  employer: 'bg-orange-100 text-orange-700',
  admin: 'bg-gray-100 text-gray-700',
};

// ============================================================================
// COMPONENT
// ============================================================================

export function CaseNotesPanel({
  referralId,
  currentUserRole = 'admin',
  readOnly = false,
  showPrivateNotes = true,
  className = '',
}: CaseNotesPanelProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [notes, setNotes] = useState<CaseNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [noteType, setNoteType] = useState<NoteType>('update');
  const [isPrivate, setIsPrivate] = useState(false);

  // Filter state
  const [filterType, setFilterType] = useState<NoteType | 'all'>('all');
  const [filterStakeholder, setFilterStakeholder] = useState<StakeholderView>('all');
  const [expandedNotes, setExpandedNotes] = useState<Set<number>>(new Set());

  // Load notes
  const loadNotes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get<CaseNote[]>(
        `/api/service-referrals/${referralId}/notes`
      );
      setNotes(response.data);
    } catch (err) {
      console.error('Failed to load case notes:', err);
      setError(t('serviceReferrals.caseNotes.errors.loadFailed', 'Failed to load case notes'));
    } finally {
      setLoading(false);
    }
  }, [referralId]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  // Submit new note
  const handleSubmitNote = async () => {
    if (!noteContent.trim()) return;

    setSubmitting(true);
    setError(null);
    try {
      await apiClient.post(`/api/service-referrals/${referralId}/notes`, {
        note_type: noteType,
        note_content: noteContent.trim(),
        is_private: isPrivate,
      });
      setNoteContent('');
      setNoteType('update');
      setIsPrivate(false);
      setShowForm(false);
      await loadNotes();
    } catch (err) {
      console.error('Failed to create note:', err);
      setError(t('serviceReferrals.caseNotes.errors.createFailed', 'Failed to create note'));
    } finally {
      setSubmitting(false);
    }
  };

  // Filter notes
  const filteredNotes = notes.filter(note => {
    // Filter by type
    if (filterType !== 'all' && note.note_type !== filterType) return false;

    // Filter by stakeholder
    if (filterStakeholder !== 'all' && note.stakeholder_type !== filterStakeholder) return false;

    // Hide private notes if not allowed
    if (note.is_private && !showPrivateNotes) return false;

    // Employer PHI shielding - only show non-sensitive notes
    if (currentUserRole === 'employer' && (note.is_private || note.note_type === 'internal')) {
      return false;
    }

    return true;
  });

  // Toggle note expansion
  const toggleNoteExpansion = (noteId: number) => {
    setExpandedNotes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(noteId)) {
        newSet.delete(noteId);
      } else {
        newSet.add(noteId);
      }
      return newSet;
    });
  };

  // Get note type display
  const getNoteTypeDisplay = (type: NoteType) => {
    const typeInfo = NOTE_TYPES.find(t => t.value === type);
    return typeInfo || { label: type, icon: FileText };
  };

  // Format timestamp
  const formatTimestamp = (dateStr: string) => {
    return fmt.dateTime(dateStr);
  };

  // Render note card
  const renderNoteCard = (note: CaseNote) => {
    const typeInfo = getNoteTypeDisplay(note.note_type);
    const TypeIcon = typeInfo.icon;
    const isExpanded = expandedNotes.has(note.id);
    const isLongContent = note.note_content.length > 200;

    return (
      <div
        key={note.id}
        className={`
          p-4 rounded-lg border
          ${note.is_private ? 'bg-gray-50 border-gray-300' : 'bg-white border-gray-200'}
          ${note.note_type === 'escalation' ? 'border-l-4 border-l-red-500' : ''}
          ${note.note_type === 'follow_up' ? 'border-l-4 border-l-yellow-500' : ''}
        `}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="flex items-center gap-1">
              <TypeIcon className="h-3 w-3" />
              {typeInfo.label}
            </Badge>
            {note.stakeholder_type && (
              <Badge className={STAKEHOLDER_COLORS[note.stakeholder_type] || 'bg-gray-100'}>
                {note.stakeholder_type.replace('_', ' ')}
              </Badge>
            )}
            {note.is_private && (
              <Badge variant="outline" className="text-gray-500">
                <Lock className="h-3 w-3 mr-1" />
                Private
              </Badge>
            )}
          </div>
          <span className="text-xs text-gray-500 whitespace-nowrap">
            {formatTimestamp(note.created_at)}
          </span>
        </div>

        {/* Content */}
        <div className="text-sm text-gray-700">
          {isLongContent && !isExpanded ? (
            <>
              {note.note_content.slice(0, 200)}...
              <button
                className="text-blue-600 hover:underline ml-1"
                onClick={() => toggleNoteExpansion(note.id)}
              >
                Show more
              </button>
            </>
          ) : (
            <>
              {note.note_content}
              {isLongContent && (
                <button
                  className="text-blue-600 hover:underline ml-1 block mt-1"
                  onClick={() => toggleNoteExpansion(note.id)}
                >
                  Show less
                </button>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <User className="h-3 w-3" />
            {note.user_name || 'Unknown'}
            {note.user_role && <span className="text-gray-400">({note.user_role})</span>}
          </span>
          {note.updated_at && note.updated_at !== note.created_at && (
            <span>Edited {formatTimestamp(note.updated_at)}</span>
          )}
        </div>
      </div>
    );
  };

  // ========================================================================
  // PHI SHIELDED VIEW FOR EMPLOYERS
  // ========================================================================

  const renderEmployerView = () => (
    <Alert className="bg-amber-50 border-amber-200">
      <Shield className="h-4 w-4 text-amber-600" />
      <AlertDescription className="text-amber-800">
        <strong>PHI Protected View:</strong> Some case notes contain protected health information
        and are not visible to employers. You can view general progress updates and service status
        without accessing detailed clinical information.
      </AlertDescription>
    </Alert>
  );

  // ========================================================================
  // MAIN RENDER
  // ========================================================================

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-500">Loading case notes...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              {t('serviceReferrals.caseNotes.title', 'Case Notes')}
            </CardTitle>
            <CardDescription>
              {t('serviceReferrals.caseNotes.description', 'Multi-stakeholder documentation and communication')}
            </CardDescription>
          </div>
          {!readOnly && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowForm(!showForm)}
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('serviceReferrals.caseNotes.addNote', 'Add Note')}
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* PHI Notice for Employers */}
        {currentUserRole === 'employer' && renderEmployerView()}

        {/* New Note Form */}
        {showForm && !readOnly && (
          <Card className="bg-gray-50">
            <CardContent className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('serviceReferrals.caseNotes.noteType', 'Note Type')}</Label>
                  <Select value={noteType} onValueChange={(v) => setNoteType(v as NoteType)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {NOTE_TYPES.map((type) => {
                        const Icon = type.icon;
                        return (
                          <SelectItem key={type.value} value={type.value}>
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4" />
                              {type.label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end pb-2">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="private-note"
                      checked={isPrivate}
                      onCheckedChange={setIsPrivate}
                    />
                    <Label htmlFor="private-note" className="flex items-center gap-1">
                      <Lock className="h-4 w-4" />
                      {t('serviceReferrals.caseNotes.privateNote', 'Private Note')}
                    </Label>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('serviceReferrals.caseNotes.noteContent', 'Note Content')}</Label>
                <Textarea
                  placeholder={t('serviceReferrals.caseNotes.notePlaceholder', 'Enter your note...')}
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  rows={4}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowForm(false)}>
                  {t('serviceReferrals.common.cancel', 'Cancel')}
                </Button>
                <Button onClick={handleSubmitNote} disabled={submitting || !noteContent.trim()}>
                  {submitting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  {t('serviceReferrals.caseNotes.saveNote', 'Save Note')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 py-2">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <Select value={filterType} onValueChange={(v) => setFilterType(v as any)}>
              <SelectTrigger className="w-[150px] h-8">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {NOTE_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Select value={filterStakeholder} onValueChange={(v) => setFilterStakeholder(v as any)}>
            <SelectTrigger className="w-[180px] h-8">
              <SelectValue placeholder="All Stakeholders" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stakeholders</SelectItem>
              <SelectItem value="provider">Provider</SelectItem>
              <SelectItem value="member">Member</SelectItem>
              <SelectItem value="program_manager">Program Manager</SelectItem>
              <SelectItem value="employer">Employer</SelectItem>
            </SelectContent>
          </Select>

          <span className="text-sm text-gray-500">
            {filteredNotes.length} note{filteredNotes.length !== 1 ? 's' : ''}
          </span>
        </div>

        <Separator />

        {/* Notes List */}
        {filteredNotes.length > 0 ? (
          <div className="space-y-3">
            {filteredNotes.map(renderNoteCard)}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <MessageSquare className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>{t('serviceReferrals.caseNotes.noNotes', 'No case notes yet')}</p>
            <p className="text-sm mt-1">
              {readOnly ? t('serviceReferrals.caseNotes.noNotesReadOnly', 'No notes have been added to this referral.') : t('serviceReferrals.caseNotes.addFirstNote', 'Add the first note to start documenting.')}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default CaseNotesPanel;
